import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtNum } from "../lib/api";
import { Surface, Btn, Field, Input, Select, Textarea } from "./Primitives";
import { Plus, Trash, Check, Warning, Translate } from "@phosphor-icons/react";

const PART_CATEGORIES = ["engine", "propeller", "airframe", "avionics", "landing_gear", "other"];
const MAINT_CATEGORIES = ["inspection", "service", "overhaul", "replacement", "ad_sb", "custom"];
const STATUSES = ["active", "snoozed"];

// Whitelist of fields the backend Patch/Create models accept. Stripping any
// other keys (id/tenant_id/aircraft_id/created_at/updated_at/…) is critical
// because the Pydantic models declare `extra="forbid"` and a single stray
// field 422s the whole save.
const PART_WRITE_FIELDS = [
  "name", "part_number", "serial_number", "category", "installed_date",
  "installed_at_hours", "installed_at_cycles", "life_limit_hours",
  "life_limit_cycles", "life_limit_calendar_months", "life_limit_calendar_date",
  "status", "notes",
];
const MAINT_WRITE_FIELDS = [
  "name", "category", "interval_hours", "interval_cycles",
  "interval_calendar_months", "last_done_date", "last_done_at_hours",
  "last_done_at_cycles", "is_owner_defined", "status", "notes",
];

function pickWritable(obj, allowed) {
  const out = {};
  for (const k of allowed) {
    const v = obj?.[k];
    if (v === undefined || v === "") continue;
    out[k] = v;
  }
  return out;
}

const SEV_BADGE = {
  expired:     { label: "EXPIRED", cls: "bg-red-500/30 text-red-200 border-red-500/60" },
  urgent_7d:   { label: "URGENT",  cls: "bg-orange-500/30 text-orange-200 border-orange-500/60" },
  warning_30d: { label: "30D",     cls: "bg-amber/20 text-amber border-amber/60" },
};

function SeverityPill({ severity }) {
  if (!severity) return <span className="text-[10px] text-zinc-600 font-mono">—</span>;
  const s = SEV_BADGE[severity];
  return (
    <span className={`tag font-mono text-[10px] tracking-wider border ${s.cls}`} style={{ padding: "2px 8px" }}>
      {s.label}
    </span>
  );
}

function formatDue(item) {
  const bits = [];
  if (item.due_in_hours != null) {
    const h = item.due_in_hours;
    bits.push(h <= 0 ? `${Math.abs(h).toFixed(1)}h over` : `${h.toFixed(1)}h left`);
  }
  if (item.due_in_days != null) {
    const d = item.due_in_days;
    bits.push(d <= 0 ? `${Math.abs(d)}d ago` : `in ${d}d`);
  }
  if (item.due_in_cycles != null) {
    const c = item.due_in_cycles;
    bits.push(c <= 0 ? `${Math.abs(c)} cyc over` : `${c} cyc left`);
  }
  return bits.length ? bits.join(" · ") : "—";
}

export default function MaintenanceTab({ aircraftId, isOwner, aircraft }) {
  const [parts, setParts] = useState([]);
  const [items, setItems] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [editingPart, setEditingPart] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [markingDone, setMarkingDone] = useState(null);
  const [doneForm, setDoneForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 4500);
  };

  const load = useCallback(async () => {
    const [p, i, fc] = await Promise.all([
      api.get(`/aircraft/${aircraftId}/parts`).then((r) => r.data),
      api.get(`/aircraft/${aircraftId}/maintenance-items`).then((r) => r.data),
      api.get(`/aircraft/${aircraftId}/maintenance-forecast`).then((r) => r.data),
    ]);
    setParts(p);
    setItems(i);
    setForecast(fc);
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  const forecastByPartId = useMemo(() => {
    const m = {};
    (forecast?.parts || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [forecast]);
  const forecastByItemId = useMemo(() => {
    const m = {};
    (forecast?.items || []).forEach((i) => { m[i.id] = i; });
    return m;
  }, [forecast]);

  const savePart = async () => {
    if (!editingPart) return;
    const isEdit = editingPart._editing === "edit" && editingPart.id;
    const payload = pickWritable(editingPart, PART_WRITE_FIELDS);
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/aircraft/${aircraftId}/parts/${editingPart.id}`, payload);
      } else {
        await api.post(`/aircraft/${aircraftId}/parts`, payload);
      }
      setEditingPart(null);
      showToast(isEdit ? "Part updated." : "Part added.");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Save failed";
      showToast(`Save failed — ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const removePart = async (pid) => {
    if (!confirm("Remove this part? History stays in the audit log.")) return;
    setBusy(true);
    try {
      await api.delete(`/aircraft/${aircraftId}/parts/${pid}`);
      showToast("Part removed.");
      load();
    } finally { setBusy(false); }
  };

  const saveItem = async () => {
    if (!editingItem) return;
    const isEdit = editingItem._editing === "edit" && editingItem.id;
    const payload = pickWritable(editingItem, MAINT_WRITE_FIELDS);
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/aircraft/${aircraftId}/maintenance-items/${editingItem.id}`, payload);
      } else {
        await api.post(`/aircraft/${aircraftId}/maintenance-items`, payload);
      }
      setEditingItem(null);
      showToast(isEdit ? "Item updated." : "Item added.");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Save failed";
      showToast(`Save failed — ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (mid) => {
    if (!confirm("Remove this maintenance item?")) return;
    setBusy(true);
    try {
      await api.delete(`/aircraft/${aircraftId}/maintenance-items/${mid}`);
      showToast("Item removed.");
      load();
    } finally { setBusy(false); }
  };

  const submitMarkDone = async () => {
    if (!markingDone) return;
    setBusy(true);
    try {
      await api.post(
        `/aircraft/${aircraftId}/maintenance-items/${markingDone.id}/mark-done`,
        doneForm,
      );
      setMarkingDone(null);
      setDoneForm({});
      showToast("Recorded as completed.");
      load();
    } finally { setBusy(false); }
  };

  const translateAll = async () => {
    if (!confirm("Translate all parts + maintenance items on this aircraft to English? This can take ~20s on large fleets.")) return;
    setTranslating(true);
    try {
      const [p, m] = await Promise.all([
        api.post(`/aircraft/${aircraftId}/parts/translate-to-english`, {}).then((r) => r.data),
        api.post(`/aircraft/${aircraftId}/maintenance-items/translate-to-english`, {}).then((r) => r.data),
      ]);
      const total = (p?.translated || 0) + (m?.translated || 0);
      const scanned = (p?.scanned || 0) + (m?.scanned || 0);
      if (scanned === 0) {
        showToast("No parts or maintenance items found on this aircraft to translate.");
      } else if (total === 0) {
        showToast(`Scanned ${scanned} field(s) — everything is already in English. Nothing to change.`);
      } else {
        showToast(`Translated ${total} field(s) (parts: ${p?.translated || 0}, items: ${m?.translated || 0}).`);
      }
      load();
    } catch (e) {
      showToast(`Translate failed — ${e?.response?.data?.detail || e?.message || "error"}`);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Forecast summary */}
      {forecast && (
        <Surface className="p-6" data-testid="maint-forecast-summary">
          <div className="flex justify-between items-baseline mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Forecast snapshot</div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">
                Tach <span className="text-zinc-300">{fmtNum(forecast.tach_time, 1)}h</span> ·
                Engine <span className="text-zinc-300">{fmtNum(forecast.engine_total_hours, 1)}h</span> ·
                Cycles <span className="text-zinc-300">{forecast.total_cycles ?? "—"}</span>
              </div>
            </div>
            <div className="flex gap-4">
              {["expired", "urgent_7d", "warning_30d"].map((sev) => {
                const n = [...(forecast.parts || []), ...(forecast.items || [])]
                  .filter((x) => x.severity === sev).length;
                return (
                  <div key={sev} className="text-right" data-testid={`forecast-count-${sev}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{SEV_BADGE[sev].label}</div>
                    <div className={`text-2xl font-mono ${sev === "expired" ? "text-red-400" : sev === "urgent_7d" ? "text-orange-400" : "text-amber"}`}>{n}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </Surface>
      )}

      {/* Parts table */}
      <Surface className="p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Tracked parts &amp; components</div>
            <div className="text-xs text-zinc-500 mt-1">Multi-criteria life limits: hours (tach for non-engine / engine clock for engine), cycles, calendar.</div>
          </div>
          {isOwner && (
            <div className="flex gap-2 flex-wrap">
              <Btn variant="ghost" onClick={translateAll} disabled={translating} data-testid="translate-maintenance-btn" title="Translate all part + maintenance-item names/notes on this aircraft to English">
                <Translate size={14} className="inline mr-1.5" />{translating ? "Translating…" : "Translate to English"}
              </Btn>
              <Btn onClick={() => setEditingPart({ name: "", category: "engine", status: "active", _editing: "new" })} data-testid="add-part-btn">
                <Plus size={14} className="inline mr-1.5" />Add part
              </Btn>
            </div>
          )}
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-800">
              <th className="text-left py-2.5 px-3">Name</th>
              <th className="text-left py-2.5 px-3">Category</th>
              <th className="text-left py-2.5 px-3">Installed</th>
              <th className="text-left py-2.5 px-3">Life limit</th>
              <th className="text-left py-2.5 px-3">Status</th>
              <th className="text-left py-2.5 px-3">Forecast</th>
              <th className="text-right py-2.5 px-3"></th>
            </tr>
          </thead>
          <tbody data-testid="parts-table">
            {parts.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-zinc-500">No parts tracked yet.</td></tr>}
            {parts.map((p) => {
              const f = forecastByPartId[p.id] || {};
              return (
                <tr key={p.id} className="border-b border-zinc-900" data-testid={`part-row-${p.id}`}>
                  <td className="py-2.5 px-3">
                    <div className="text-zinc-200">{p.name}</div>
                    {p.part_number && <div className="text-[10px] text-zinc-500 font-mono">P/N {p.part_number}{p.serial_number ? ` · S/N ${p.serial_number}` : ""}</div>}
                  </td>
                  <td className="py-2.5 px-3 capitalize text-zinc-400">{p.category?.replace("_", " ")}</td>
                  <td className="py-2.5 px-3 font-mono text-xs text-zinc-400">{fmtDate(p.installed_date) || "—"}{p.installed_at_hours != null && <span className="text-zinc-600"> @ {fmtNum(p.installed_at_hours, 1)}h</span>}</td>
                  <td className="py-2.5 px-3 font-mono text-xs text-zinc-400">
                    {p.life_limit_hours != null && <div>{fmtNum(p.life_limit_hours, 0)}h</div>}
                    {p.life_limit_cycles != null && <div>{p.life_limit_cycles} cyc</div>}
                    {p.life_limit_calendar_date && <div>{p.life_limit_calendar_date}</div>}
                    {p.life_limit_calendar_months != null && <div>{p.life_limit_calendar_months} mo</div>}
                    {!p.life_limit_hours && !p.life_limit_cycles && !p.life_limit_calendar_date && !p.life_limit_calendar_months && "—"}
                  </td>
                  <td className="py-2.5 px-3 text-xs uppercase tracking-wider text-zinc-400">{p.status}</td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <SeverityPill severity={f.severity} />
                      <span className="text-xs text-zinc-400 font-mono">{formatDue(f)}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {isOwner && (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditingPart({ ...p, _editing: "edit" })} className="text-zinc-400 hover:text-amber text-[10px] uppercase tracking-[0.2em] font-mono" data-testid={`edit-part-${p.id}`}>Edit</button>
                        <button onClick={() => removePart(p.id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-part-${p.id}`}><Trash size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </Surface>

      {/* Maintenance items table */}
      <Surface className="p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Inspection &amp; maintenance program</div>
            <div className="text-xs text-zinc-500 mt-1">Recurring annual / 100-h / 50-h / owner-defined items. &quot;Mark done&quot; auto-fills today + current tach.</div>
          </div>
          {isOwner && (
            <Btn onClick={() => setEditingItem({ name: "", category: "inspection", status: "active", _editing: "new" })} data-testid="add-maint-item-btn">
              <Plus size={14} className="inline mr-1.5" />Add item
            </Btn>
          )}
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-800">
              <th className="text-left py-2.5 px-3">Name</th>
              <th className="text-left py-2.5 px-3">Category</th>
              <th className="text-left py-2.5 px-3">Interval</th>
              <th className="text-left py-2.5 px-3">Last done</th>
              <th className="text-left py-2.5 px-3">Forecast</th>
              <th className="text-right py-2.5 px-3"></th>
            </tr>
          </thead>
          <tbody data-testid="maint-items-table">
            {items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-zinc-500">No maintenance items defined yet.</td></tr>}
            {items.map((m) => {
              const f = forecastByItemId[m.id] || {};
              return (
                <tr key={m.id} className="border-b border-zinc-900" data-testid={`maint-item-row-${m.id}`}>
                  <td className="py-2.5 px-3">
                    <div className="text-zinc-200">{m.name}</div>
                    {m.is_owner_defined && <div className="text-[10px] text-amber font-mono uppercase tracking-wider">custom</div>}
                  </td>
                  <td className="py-2.5 px-3 capitalize text-zinc-400">{m.category?.replace("_", " ")}</td>
                  <td className="py-2.5 px-3 font-mono text-xs text-zinc-400">
                    {m.interval_hours && <div>{m.interval_hours}h</div>}
                    {m.interval_cycles && <div>{m.interval_cycles} cyc</div>}
                    {m.interval_calendar_months && <div>{m.interval_calendar_months} mo</div>}
                    {!m.interval_hours && !m.interval_cycles && !m.interval_calendar_months && "—"}
                  </td>
                  <td className="py-2.5 px-3 font-mono text-xs text-zinc-400">
                    {m.last_done_date ? (<><div>{m.last_done_date}</div>{m.last_done_at_hours != null && <div className="text-zinc-600">@ {fmtNum(m.last_done_at_hours, 1)}h</div>}</>) : "—"}
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <SeverityPill severity={f.severity} />
                      <span className="text-xs text-zinc-400 font-mono">{formatDue(f)}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {isOwner && (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => { setMarkingDone(m); setDoneForm({}); }} className="text-amber hover:underline text-[10px] uppercase tracking-[0.2em] font-mono" data-testid={`mark-done-${m.id}`}><Check size={11} className="inline mr-1" />Done</button>
                        <button onClick={() => setEditingItem({ ...m, _editing: "edit" })} className="text-zinc-400 hover:text-amber text-[10px] uppercase tracking-[0.2em] font-mono" data-testid={`edit-maint-${m.id}`}>Edit</button>
                        <button onClick={() => removeItem(m.id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-maint-${m.id}`}><Trash size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </Surface>

      {/* Part edit modal */}
      {editingPart && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="part-modal">
          <Surface className="w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-medium mb-4">{editingPart._editing === "edit" ? "Edit part" : "Add part"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name *"><Input value={editingPart.name || ""} onChange={(e) => setEditingPart({ ...editingPart, name: e.target.value })} data-testid="part-name-input" /></Field>
              <Field label="Category *">
                <Select value={editingPart.category || "engine"} onChange={(e) => setEditingPart({ ...editingPart, category: e.target.value })} data-testid="part-category-input">
                  {PART_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
                </Select>
              </Field>
              <Field label="Part number"><Input value={editingPart.part_number || ""} onChange={(e) => setEditingPart({ ...editingPart, part_number: e.target.value })} data-testid="part-pn-input" /></Field>
              <Field label="Serial number"><Input value={editingPart.serial_number || ""} onChange={(e) => setEditingPart({ ...editingPart, serial_number: e.target.value })} data-testid="part-sn-input" /></Field>
              <Field label="Installed date"><Input type="date" value={editingPart.installed_date || ""} onChange={(e) => setEditingPart({ ...editingPart, installed_date: e.target.value })} data-testid="part-installed-date" /></Field>
              <Field label="Installed @ hours" hint={editingPart.category === "engine" ? `engine clock (now ${aircraft?.engine_total_hours ?? "—"}h)` : `tach (now ${aircraft?.tach_time ?? "—"}h)`}>
                <Input type="number" step="0.1" value={editingPart.installed_at_hours ?? ""} onChange={(e) => setEditingPart({ ...editingPart, installed_at_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="part-installed-hours" />
              </Field>
              <Field label="Installed @ cycles"><Input type="number" value={editingPart.installed_at_cycles ?? ""} onChange={(e) => setEditingPart({ ...editingPart, installed_at_cycles: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></Field>
              <Field label="Life limit (hours)" hint="Total hours of life from new"><Input type="number" step="0.1" value={editingPart.life_limit_hours ?? ""} onChange={(e) => setEditingPart({ ...editingPart, life_limit_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="part-limit-hours" /></Field>
              <Field label="Life limit (cycles)"><Input type="number" value={editingPart.life_limit_cycles ?? ""} onChange={(e) => setEditingPart({ ...editingPart, life_limit_cycles: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></Field>
              <Field label="Life limit (calendar months)"><Input type="number" value={editingPart.life_limit_calendar_months ?? ""} onChange={(e) => setEditingPart({ ...editingPart, life_limit_calendar_months: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="part-limit-months" /></Field>
              <Field label="Life limit (absolute date)"><Input type="date" value={editingPart.life_limit_calendar_date || ""} onChange={(e) => setEditingPart({ ...editingPart, life_limit_calendar_date: e.target.value })} data-testid="part-limit-date" /></Field>
              <Field label="Status">
                <Select value={editingPart.status || "active"} onChange={(e) => setEditingPart({ ...editingPart, status: e.target.value })}>
                  <option value="active">active</option>
                  <option value="removed">removed</option>
                  <option value="scrapped">scrapped</option>
                </Select>
              </Field>
            </div>
            <div className="mt-3"><Field label="Notes"><Textarea rows={2} value={editingPart.notes || ""} onChange={(e) => setEditingPart({ ...editingPart, notes: e.target.value })} /></Field></div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setEditingPart(null)}>Cancel</Btn>
              <Btn onClick={savePart} disabled={busy} data-testid="part-save-btn">{busy ? "Saving…" : (editingPart._editing === "edit" ? "Save" : "Add part")}</Btn>
            </div>
          </Surface>
        </div>
      )}

      {/* Maintenance item edit modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="maint-modal">
          <Surface className="w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-medium mb-4">{editingItem._editing === "edit" ? "Edit maintenance item" : "Add maintenance item"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name *"><Input value={editingItem.name || ""} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} data-testid="maint-name-input" /></Field>
              <Field label="Category *">
                <Select value={editingItem.category || "inspection"} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} data-testid="maint-category-input">
                  {MAINT_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
                </Select>
              </Field>
              <Field label="Interval (hours)"><Input type="number" step="0.1" value={editingItem.interval_hours ?? ""} onChange={(e) => setEditingItem({ ...editingItem, interval_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="maint-interval-hours" /></Field>
              <Field label="Interval (cycles)"><Input type="number" value={editingItem.interval_cycles ?? ""} onChange={(e) => setEditingItem({ ...editingItem, interval_cycles: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></Field>
              <Field label="Interval (calendar months)"><Input type="number" value={editingItem.interval_calendar_months ?? ""} onChange={(e) => setEditingItem({ ...editingItem, interval_calendar_months: e.target.value === "" ? null : parseInt(e.target.value, 10) })} data-testid="maint-interval-months" /></Field>
              <Field label="Status">
                <Select value={editingItem.status || "active"} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Last done date"><Input type="date" value={editingItem.last_done_date || ""} onChange={(e) => setEditingItem({ ...editingItem, last_done_date: e.target.value })} data-testid="maint-last-date" /></Field>
              <Field label="Last done @ hours (tach)"><Input type="number" step="0.1" value={editingItem.last_done_at_hours ?? ""} onChange={(e) => setEditingItem({ ...editingItem, last_done_at_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="maint-last-hours" /></Field>
            </div>
            <div className="mt-3"><Field label="Notes"><Textarea rows={2} value={editingItem.notes || ""} onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })} /></Field></div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setEditingItem(null)}>Cancel</Btn>
              <Btn onClick={saveItem} disabled={busy} data-testid="maint-save-btn">{busy ? "Saving…" : (editingItem._editing === "edit" ? "Save" : "Add item")}</Btn>
            </div>
          </Surface>
        </div>
      )}

      {/* Mark-done modal */}
      {markingDone && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="mark-done-modal">
          <Surface className="w-full max-w-md p-8">
            <h3 className="text-xl font-medium mb-2">Mark done: {markingDone.name}</h3>
            <div className="text-sm text-zinc-500 mb-5 flex items-start gap-2">
              <Warning size={14} className="mt-0.5 text-amber flex-shrink-0" />
              <span>Leave fields empty to auto-fill from today + current tach <span className="text-zinc-300 font-mono">({aircraft?.tach_time ?? "—"}h)</span>.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Date (default = today)"><Input type="date" value={doneForm.date || ""} onChange={(e) => setDoneForm({ ...doneForm, date: e.target.value })} data-testid="mark-done-date" /></Field>
              <Field label="At hours (default = tach)"><Input type="number" step="0.1" value={doneForm.at_hours ?? ""} onChange={(e) => setDoneForm({ ...doneForm, at_hours: e.target.value === "" ? undefined : parseFloat(e.target.value) })} data-testid="mark-done-hours" /></Field>
              <div className="col-span-2"><Field label="Notes"><Input value={doneForm.notes || ""} onChange={(e) => setDoneForm({ ...doneForm, notes: e.target.value })} /></Field></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setMarkingDone(null)}>Cancel</Btn>
              <Btn onClick={submitMarkDone} disabled={busy} data-testid="mark-done-submit">{busy ? "Saving…" : "Record completion"}</Btn>
            </div>
          </Surface>
        </div>
      )}

      {/* Toast for save/translate feedback */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 border border-amber/40 bg-[#1a1209] text-amber text-xs font-mono shadow-xl" data-testid="maint-action-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
